import {
    ReactNode,
    createContext,
    useContext,
    useState,
    PropsWithChildren,
    useMemo
} from "react";

import { css } from "@compiled/react";

import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowTouchpointProvider } from "@/components/cross-flow/touchpoint";
import { SiteSelectorContextProvider } from "@/components/site-selector-modal";
import { SitesContextProvider } from "@/components/sites-provider";
import { GlobalFooter } from "@/patterns/global-footer/src";

import ConfluenceSoftwareNav from "../../../../confluence-software-page/src/sections/confluence-navbar";

const minHeight = css({
    minHeight: 1000
});

// Confluence Template Experiment code ================================\
export type ConfluenceTemplateExperimentContextType = {
    templateNameExternal: string;
    pluginModuleKey: string;
};

const ConfluenceTemplateExperimentContext = createContext<{
    template: ConfluenceTemplateExperimentContextType | undefined;
    setTemplate: (templates: ConfluenceTemplateExperimentContextType) => void;
}>({
    template: undefined,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setTemplate: () => {}
});

export const ConfluenceTemplateExperimentContextProvider = ({
    children
}: PropsWithChildren) => {
    const [template, setTemplate] = useState<
        ConfluenceTemplateExperimentContextType | undefined
    >(undefined);

    const value = useMemo(
        () => ({
            template,
            setTemplate
        }),
        [template]
    );

    return (
        <ConfluenceTemplateExperimentContext.Provider value={value}>
            {children}
        </ConfluenceTemplateExperimentContext.Provider>
    );
};

export const useConfluenceTemplateExperimentContext = () => {
    const context = useContext(ConfluenceTemplateExperimentContext);
    if (!context) {
        throw new Error(
            "useConfluenceTemplateExperimentContext must be used within a ConfluenceTemplateExperimentContextProvider"
        );
    }
    return context;
};
// ================================================

export const SharedHeaderFooterContainer = ({
    children
}: {
    children: ReactNode;
}) => {
    return (
        <div css={minHeight}>
            <ConfluenceTemplateExperimentContextProvider>
                <CrossFlowTouchpointProvider
                    productKeys={[ProductKeys.CONFLUENCE]}
                >
                    <SitesContextProvider>
                        <SiteSelectorContextProvider
                            productKey={ProductKeys.CONFLUENCE}
                            analyticsPrefix="wac.showroom"
                            internalLinkAfterIdentity="/team-playbook/plays/choose-your-site"
                        >
                            <ConfluenceSoftwareNav
                                isWrappedInSharedHeaderFooterContainer={true}
                            />
                            {children}
                        </SiteSelectorContextProvider>
                    </SitesContextProvider>
                    <GlobalFooter />
                </CrossFlowTouchpointProvider>
            </ConfluenceTemplateExperimentContextProvider>
        </div>
    );
};
